import React, { useState, useEffect } from 'react';

interface Step {
  step_name: string;
  step_order: number;
  last_run: string | null;
  status: string;
  details: string;
  articles_waiting?: number;
  articles_total?: number;
  articles_processed?: number;
  total_feeds?: number;
  active_feeds?: number;
  failing_feeds?: number;
  errors?: number;
}

interface ApiStatus {
  api_name: string;
  last_called: string | null;
  status: string;
  successes_6h: number;
  failures_6h: number;
}

interface PipelineData {
  timestamp: string;
  system: {
    cpu: number;
    memory: number;
    disk: number;
  };
  rss_curation_steps: Step[];
  content_processing_steps: Step[];
  api_status: ApiStatus[];
}

const API_BASE_URL = window.location.hostname === 'localhost'
  ? 'http://127.0.0.1:3001/api'
  : '/api';

function PipelineStepApp() {
  const [token, setToken] = useState<string | null>(localStorage.getItem('auth_token'));
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [data, setData] = useState<PipelineData | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (token) {
      fetchData();
      const interval = setInterval(fetchData, 10000); // Refresh every 10 seconds
      return () => clearInterval(interval);
    }
  }, [token]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError('');
    try {
      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });

      if (!response.ok) {
        throw new Error('Invalid credentials');
      }

      const data = await response.json();
      setToken(data.access_token);
      localStorage.setItem('auth_token', data.access_token);
    } catch (error) {
      setLoginError('Login failed. Please check your credentials.');
    }
  };

  const fetchData = async () => {
    if (!token) return;

    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/pipeline/overview`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          localStorage.removeItem('auth_token');
          setToken(null);
        }
        throw new Error('Failed to fetch data');
      }

      const json = await response.json();
      setData(json);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'running': return 'border-green-500 bg-green-50';
      case 'stuck': return 'border-red-500 bg-red-50';
      case 'idle': return 'border-gray-400 bg-gray-50';
      case 'success': return 'border-blue-500 bg-blue-50';
      case 'healthy': return 'border-green-500 bg-green-50';
      case 'unknown': return 'border-gray-400 bg-gray-50';
      default: return 'border-gray-400 bg-gray-50';
    }
  };

  const getStatusBadge = (status: string) => {
    const baseClass = "px-3 py-1 rounded-full text-sm font-semibold";
    switch (status.toLowerCase()) {
      case 'running': return `${baseClass} bg-green-500 text-white`;
      case 'stuck': return `${baseClass} bg-red-500 text-white`;
      case 'idle': return `${baseClass} bg-gray-500 text-white`;
      case 'success': return `${baseClass} bg-blue-500 text-white`;
      case 'healthy': return `${baseClass} bg-green-500 text-white`;
      case 'unknown': return `${baseClass} bg-gray-500 text-white`;
      default: return `${baseClass} bg-gray-500 text-white`;
    }
  };

  const formatTimestamp = (timestamp: string | null) => {
    if (!timestamp) return 'Never';
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffMins < 1440) return `${Math.floor(diffMins / 60)}h ago`;
    return `${Math.floor(diffMins / 1440)}d ago`;
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-400 to-blue-600">
        <div className="bg-white p-8 rounded-lg shadow-lg w-96">
          <h1 className="text-3xl font-bold text-center mb-6 text-gray-800">
            AI Daily Post
          </h1>
          <h2 className="text-xl text-center mb-6 text-gray-600">
            Pipeline Monitoring
          </h2>
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>
            {loginError && (
              <div className="text-red-600 text-sm text-center">
                {loginError}
              </div>
            )}
            <button
              type="submit"
              className="w-full bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2 px-4 rounded-md transition duration-200"
            >
              Login
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-white shadow-md">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-gray-800">
                AI Daily Post - Pipeline Monitoring
              </h1>
              <p className="text-sm text-gray-600">
                {data && formatTimestamp(data.timestamp)} • Auto-refresh: 10s
              </p>
            </div>
            {data && (
              <div className="flex gap-4 text-sm">
                <div className="text-center">
                  <div className="font-semibold text-gray-700">CPU</div>
                  <div className="text-lg text-blue-600">{data.system.cpu.toFixed(1)}%</div>
                </div>
                <div className="text-center">
                  <div className="font-semibold text-gray-700">Memory</div>
                  <div className="text-lg text-blue-600">{data.system.memory.toFixed(1)}%</div>
                </div>
                <div className="text-center">
                  <div className="font-semibold text-gray-700">Disk</div>
                  <div className="text-lg text-blue-600">{data.system.disk.toFixed(1)}%</div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6 space-y-8">
        {loading && !data && (
          <div className="text-center text-gray-600">Loading...</div>
        )}

        {data && (
          <>
            {/* RSS Curation Steps */}
            <div>
              <h2 className="text-xl font-bold text-gray-800 mb-4">
                RSS Curation Pipeline
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {data.rss_curation_steps.map((step) => (
                  <div
                    key={step.step_order}
                    className={`border-l-4 rounded-lg p-6 bg-white shadow ${getStatusColor(step.status)}`}
                  >
                    <div className="flex justify-between items-start mb-3">
                      <h3 className="text-lg font-semibold text-gray-800">
                        {step.step_name}
                      </h3>
                      <span className={getStatusBadge(step.status)}>
                        {step.status.toUpperCase()}
                      </span>
                    </div>
                    <div className="space-y-2 text-sm text-gray-700">
                      <div>
                        <span className="font-medium">Last Run:</span>{' '}
                        {formatTimestamp(step.last_run)}
                      </div>
                      <div className="font-medium text-gray-800">
                        {step.details}
                      </div>
                      {step.total_feeds !== undefined && (
                        <div className="text-xs text-gray-600">
                          Total: {step.total_feeds} | Active: {step.active_feeds} | Failed: {step.failing_feeds}
                        </div>
                      )}
                      {step.articles_waiting !== undefined && (
                        <div className="text-lg font-bold text-blue-600">
                          {step.articles_waiting} waiting
                        </div>
                      )}
                      {step.articles_processed !== undefined && (
                        <div className="text-lg font-bold text-blue-600">
                          {step.articles_processed} processed
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Content Processing Steps */}
            <div>
              <h2 className="text-xl font-bold text-gray-800 mb-4">
                Content Processing Pipeline
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
                {data.content_processing_steps.map((step) => (
                  <div
                    key={step.step_order}
                    className={`border-l-4 rounded-lg p-6 bg-white shadow ${getStatusColor(step.status)}`}
                  >
                    <div className="flex justify-between items-start mb-3">
                      <h3 className="text-sm font-semibold text-gray-800">
                        {step.step_name}
                      </h3>
                      <span className={getStatusBadge(step.status).replace('px-3 py-1', 'px-2 py-0.5 text-xs')}>
                        {step.status.toUpperCase()}
                      </span>
                    </div>
                    <div className="space-y-2 text-xs text-gray-700">
                      <div>
                        <span className="font-medium">Last:</span>{' '}
                        {formatTimestamp(step.last_run)}
                      </div>
                      {step.articles_waiting !== undefined && (
                        <div className="text-xl font-bold text-blue-600">
                          {step.articles_waiting}
                        </div>
                      )}
                      {step.articles_total !== undefined && (
                        <div className="text-xl font-bold text-blue-600">
                          {step.articles_total}
                        </div>
                      )}
                      {step.errors !== undefined && step.errors > 0 && (
                        <div className="text-red-600 font-semibold">
                          {step.errors} errors
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* API Status */}
            {data.api_status.length > 0 && (
              <div>
                <h2 className="text-xl font-bold text-gray-800 mb-4">
                  External APIs
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {data.api_status.map((api) => (
                    <div
                      key={api.api_name}
                      className={`border-l-4 rounded-lg p-4 bg-white shadow ${getStatusColor(api.status)}`}
                    >
                      <h3 className="text-sm font-semibold text-gray-800 mb-2">
                        {api.api_name}
                      </h3>
                      <div className="space-y-1 text-xs text-gray-700">
                        <div className={getStatusBadge(api.status).replace('px-3 py-1', 'px-2 py-0.5 inline-block')}>
                          {api.status.toUpperCase()}
                        </div>
                        <div>Last: {formatTimestamp(api.last_called)}</div>
                        <div className="flex justify-between">
                          <span className="text-green-600">✓ {api.successes_6h}</span>
                          <span className="text-red-600">✗ {api.failures_6h}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default PipelineStepApp;
